import { Curve, Vector3 } from 'three';

/**
 * The stair itself: a helix described once, then shared by the treads, the
 * balusters, the handrail, the encrustation and the camera. Everything in
 * Isidora that turns, turns on this.
 */
export class Helix extends Curve {
  /**
   * @param {object} o
   * @param {number} o.radius      distance from the axis
   * @param {number} o.turns       revolutions from top to bottom
   * @param {number} o.height      total descent (positive; y goes down)
   * @param {number} [o.topY]      world y of t=0
   * @param {number} [o.phase]     starting angle in radians
   * @param {number} [o.handed]    +1 clockwise seen from above, -1 widdershins
   */
  constructor({ radius, turns, height, topY = 0, phase = 0, handed = 1 }) {
    super();
    Object.assign(this, { radius, turns, height, topY, phase, handed });
  }

  /** Angle, in radians, at journey position t. */
  angleAt(t) {
    return this.phase + this.handed * t * this.turns * Math.PI * 2;
  }

  /** World y at journey position t. */
  heightAt(t) {
    return this.topY - t * this.height;
  }

  getPoint(t, target = new Vector3()) {
    const a = this.angleAt(t);
    return target.set(Math.cos(a) * this.radius, this.heightAt(t), Math.sin(a) * this.radius);
  }

  /** Point on a helix of a different radius but the same winding. */
  pointAtRadius(t, radius, target = new Vector3()) {
    const a = this.angleAt(t);
    return target.set(Math.cos(a) * radius, this.heightAt(t), Math.sin(a) * radius);
  }

  /** Unit vector pointing away from the axis. */
  radialAt(t, target = new Vector3()) {
    const a = this.angleAt(t);
    return target.set(Math.cos(a), 0, Math.sin(a));
  }

  /** Normalised direction of travel (downward along the stair). */
  tangentAt(t, target = new Vector3()) {
    const a = this.angleAt(t);
    const dA = this.handed * this.turns * Math.PI * 2;
    return target
      .set(-Math.sin(a) * this.radius * dA, -this.height, Math.cos(a) * this.radius * dA)
      .normalize();
  }

  /** Total 3-D length, used to space things evenly rather than by angle. */
  get arcLength() {
    const circumference = this.turns * Math.PI * 2 * this.radius;
    return Math.hypot(circumference, this.height);
  }
}

/**
 * A logarithmic (equiangular) spiral in the XY plane — the curve that governs
 * a nautilus, a violin scroll and a stairwell alike. Returned as points so it
 * can feed a TubeGeometry, a Shape or a line.
 */
export function logarithmicSpiral({
  turns = 2.4,
  growth = 2.3,
  startRadius = 1,
  segments = 160,
  z = 0,
} = {}) {
  const k = Math.log(growth) / (Math.PI * 2);
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * turns * Math.PI * 2;
    const r = startRadius * Math.exp(k * theta);
    pts.push(new Vector3(Math.cos(theta) * r, Math.sin(theta) * r, z));
  }
  return pts;
}
